import CircularProgress from '@material-ui/core/CircularProgress';
import {
  ArrowClockwise,
  GameController,
  SmileyIcon,
  SmileyMehIcon,
  SmileySadIcon,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import dateFormat from 'dateformat';
import { Breadcrumbs, HistoryDropdown, RedesignLayout, useSharedStyles } from '../../shared';
import RedesignImage from '../../shared/components/RedesignImage';
import { useGameSynthesis } from './hooks/useGameSynthesis';
import { useGameSynthesisStyles } from './index.styles';
import {
  formatRealizationDate,
  getEssayCountLabel,
  getGroupTitle,
  getPointsPerStepLabel,
  getRecapLabel,
  getRetainedAttempt,
} from './utils';

const RANK_ICONS = {
  1: SmileyIcon,
  2: SmileyMehIcon,
  3: SmileySadIcon,
} as const;

const GameSynthesis = () => {
  const classes = useGameSynthesisStyles();
  const sharedClasses = useSharedStyles();
  const {
    beneficiaryName,
    coverUrl,
    goBack,
    groups,
    hideRetake,
    historyItems,
    idResult,
    loading,
    recapRanks,
    restartGame,
    restarting,
    selectHistory,
    synthesis,
    totalSteps,
  } = useGameSynthesis();

  if (loading || !synthesis) {
    return (
      <RedesignLayout footer={true} noPadding={true}>
        <div className={sharedClasses.loadingCenter}>
          <CircularProgress />
        </div>
      </RedesignLayout>
    );
  }

  const percentage = Math.round(synthesis.percentage || 0);

  return (
    <RedesignLayout footer={true} noPadding={true}>
      <div className={sharedClasses.pageShell}>
        <div className={classes.topRow}>
          {hideRetake ? (
            <button type="button" className={classes.backLink} onClick={goBack}>
              ← Retour
            </button>
          ) : (
            <Breadcrumbs
              items={[
                {
                  label: 'Mes ressources',
                  href: '/mes-ressources',
                },
                { label: 'Résultat du jeu' },
              ]}
            />
          )}
          <div className={classes.actions}>
            <HistoryDropdown
              items={historyItems.map(item => {
                const date = item.createdAt ? new Date(item.createdAt) : null;
                return {
                  id: item.id,
                  label:
                    date && !Number.isNaN(date.getTime())
                      ? dateFormat(date, 'dd/mm/yyyy, HH:MM')
                      : '-',
                };
              })}
              activeId={idResult}
              onSelect={selectHistory}
            />
            {!hideRetake && (
              <button
                type="button"
                className={classes.retakeButton}
                style={{ backgroundColor: '#000A8D' }}
                onClick={restartGame}
                disabled={restarting}>
                <ArrowClockwise className={classes.actionIcon} weight="bold" />
                Recommencer
              </button>
            )}
          </div>
        </div>

        <section className={classes.hero}>
          {coverUrl && (
            <div className={classes.cover}>
              <RedesignImage src={coverUrl} alt="" className={classes.coverImage} />
            </div>
          )}
          <div className={classes.heroMain}>
            <span className={classes.gameLabel}>
              <GameController className={classes.gameLabelIcon} weight="fill" />
              SERIOUS GAME
            </span>
            <h1 className={classes.gameTitle}>{synthesis.gameTitle}</h1>
            {synthesis.serviceName && (
              <p className={classes.serviceName}>{synthesis.serviceName}</p>
            )}
            <div className={classes.metaGrid}>
              <div className={classes.metaItem}>
                <p className={classes.metaLabel}>Bénéficiaire</p>
                <p className={classes.metaValue}>{beneficiaryName}</p>
              </div>
              <div className={classes.metaItem}>
                <p className={classes.metaLabel}>Date de réalisation</p>
                <p className={classes.metaValue}>{formatRealizationDate(synthesis.completedAt)}</p>
              </div>
            </div>
          </div>
          <div className={classes.gaugeBlock}>
            <div className={classes.gaugeWrap}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={195}
                thickness={2.8}
                className={classes.gaugeTrack}
              />
              <CircularProgress
                variant="determinate"
                value={Math.max(0, Math.min(percentage, 100))}
                size={195}
                thickness={2.8}
                className={classes.gaugeValue}
              />
              <div className={classes.gaugeCenter}>
                <div className={classes.gaugeScoreRow}>
                  <span className={classes.gaugeScore}>{synthesis.scoreGlobal}</span>
                  <span className={classes.gaugeMax}>/ {synthesis.scoreMaximum}</span>
                </div>
                <p className={classes.gaugeCaption}>Score global : {percentage}%</p>
              </div>
            </div>
          </div>
        </section>

        {recapRanks.length > 0 && (
          <section>
            <h2 className={classes.sectionTitle}>Récapitulatif</h2>
            <div className={classes.recapGrid}>
              {recapRanks.map(item => {
                const Icon = RANK_ICONS[item.rank as 1 | 2 | 3] || SmileyMehIcon;
                return (
                  <article key={item.rank} className={classes.recapCard}>
                    <div
                      className={clsx(
                        classes.recapIcon,
                        item.rank === 1 && classes.recapIconRank1,
                        item.rank === 2 && classes.recapIconRank2,
                        item.rank === 3 && classes.recapIconRank3
                      )}>
                      <Icon className={classes.recapFace} weight="regular" />
                    </div>
                    <div className={classes.recapBody}>
                      <p className={classes.recapLabel}>{getRecapLabel(item.rank)}</p>
                      <p className={classes.recapSteps}>
                        <span className={classes.recapStepsCount}>{item.stepCount}</span>
                        {` / ${totalSteps} étapes`}
                      </p>
                      <p className={classes.recapPoints}>
                        {item.points} point{item.points > 1 ? 's' : ''}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        <section>
          <h2 className={classes.sectionTitle}>Détail des étapes</h2>
          <p className={classes.detailsIntro}>
            Réparties selon la tentative qui a abouti à la bonne réponse.
          </p>
          {groups.map(group => (
            <div key={group.rank} className={classes.group}>
              <div className={classes.groupHeader}>
                <div>
                  <h3 className={classes.groupTitle}>
                    {getGroupTitle(group.steps.length, group.rank)}
                  </h3>
                  <p className={classes.groupPoints}>{getPointsPerStepLabel(group.points)}</p>
                </div>
                <div className={classes.pills} aria-hidden={true}>
                  {[1, 2, 3].map(index => (
                    <span
                      key={index}
                      className={clsx(
                        classes.pill,
                        index < group.rank && classes.pillError,
                        index === group.rank && classes.pillSuccess
                      )}
                    />
                  ))}
                  <span className={classes.pillsLabel}>{getEssayCountLabel(group.rank)}</span>
                </div>
              </div>
              <div className={classes.stepsGrid}>
                {group.steps.map(step => {
                  const choice = getRetainedAttempt(step);
                  return (
                    <article key={step.pageId} className={classes.stepCard}>
                      <div className={classes.stepHeader}>
                        <div className={classes.stepHeaderMain}>
                          <span className={classes.stepNumber}>Étape {step.stepNumber}</span>
                          <p className={classes.stepTitle}>{step.stepTitle}</p>
                        </div>
                      </div>
                      {choice && (
                        <div className={classes.stepBody}>
                          <div className={classes.choiceBox}>
                            <span className={classes.choiceNumber}>
                              Choix {choice.choiceNumber}
                            </span>
                            {choice.choiceLabel ? (
                              <>
                                {' - '}
                                <span
                                  className={classes.choiceLabel}
                                  dangerouslySetInnerHTML={{ __html: choice.choiceLabel }}
                                />
                              </>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      </div>
    </RedesignLayout>
  );
};

export default GameSynthesis;
